import Image from 'next/image'
import { forwardRef, type ComponentProps } from 'react'
import { Badge } from 'ui/src/components/Badge/Badge'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { Video, type VideoProps, type VideoRef } from '@/components/Video/Video'
import { story, tagsContainer } from './Story.css'
import type { Item, VideoItem, ImageItem } from './StoryCarousel.types'

export type VideoStoryProps = { item: VideoItem } & Omit<
  VideoProps,
  'sources' | 'loop' | 'showControls' | 'muted' | 'resetOnEnd' | 'poster'
>

export const VideoStory = forwardRef<VideoRef, VideoStoryProps>(({ item, ...delegated }, ref) => {
  return (
    <div className={story}>
      <StoryTags tags={item.tags} />
      <Video
        ref={ref}
        sources={item.asset.sources}
        loop={item.asset.loop}
        showControls={false}
        muted={true}
        resetOnEnd={false}
        poster={item.asset.poster}
        {...delegated}
      />
      <StoryDescription title={item.title} subtitle={item.subtitle} />
    </div>
  )
})
VideoStory.displayName = 'VideoStory'

export type ImageStoryProps = { item: ImageItem } & Omit<ComponentProps<typeof Image>, 'src' | 'alt'>

export function ImageStory({ item, ...delegated }: ImageStoryProps) {
  return (
    <div className={story}>
      <StoryTags tags={item.tags} />
      {/* Next's image requires a positioned container for 'fill' to work properly */}
      <div className={sprinkles({ position: 'relative', flexGrow: 1 })}>
        <Image src={item.asset.src} alt={item.asset.alt} fill={true} objectFit="cover" {...delegated} />
      </div>
      <StoryDescription title={item.title} subtitle={item.subtitle} />
    </div>
  )
}

function StoryTags({ tags }: { tags: Item['tags'] }) {
  if (!tags || tags.length === 0) return null

  return (
    <div className={tagsContainer}>
      {tags.map((tag) => (
        <Badge key={tag} color="white" size="small">
          {tag}
        </Badge>
      ))}
    </div>
  )
}

function StoryDescription({ title, subtitle }: Pick<Item, 'title' | 'subtitle'>) {
  if (!title && !subtitle) return null

  return (
    <div>
      {title && (
        <Heading as="h3" color="textPrimary" variant="standard.18" align="center">
          {title}
        </Heading>
      )}
      {subtitle && (
        <Text as="p" color="textSecondary" align="center" balance={true}>
          {subtitle}
        </Text>
      )}
    </div>
  )
}
